import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {ANTIBODY_REST_PROPS_BY_ID} from 'antibodies/AntibodyConstants';
import {useAntibodyFormatsQuery} from 'antibodies/hooks/useAntibodyFormatsQuery';
import AntibodyConfigurationTable from 'antibodies/schema-configuration/AntibodyConfigurationTable';
import AntibodyFormatPicker from 'antibodies/schema-configuration/AntibodyFormatPicker';
import {
  ANTIBODY_SCHEMA_INTERFACE_ID,
  DraftImplementedAntibodyInterface,
  UNSET_ANTIBODY_INTERFACE_ID,
} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {AntibodyProps} from 'antibodies/visualization/Antibody';
import {Header3} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';

interface AntibodyConfigurationCheckboxProps {
  checked: boolean;
  disabled: boolean;
  onChange: () => void;
}

/**
 * Exported for testing
 * @package
 */
export function AntibodyConfigurationCheckbox({
  checked,
  disabled,
  onChange,
}: AntibodyConfigurationCheckboxProps): JSX.Element {
  return (
    <CheckboxInput
      className="u-tiny-spacing-bottom"
      id="configure-antibody-checkbox"
      label="Configure as antibody"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export interface AntibodyConfigurationProps {
  antibodyInterfaceImplementation: DraftImplementedAntibodyInterface | null;
  disabled: boolean;
  onChange: (config: DraftImplementedAntibodyInterface | null) => void;
  /* All tag schemas that currently exist on the registry */
  allSchemas: Array<TagSchemaSummaryGraphQL>;
}

export default function AntibodySchemaConfiguration({
  antibodyInterfaceImplementation,
  disabled,
  onChange,
  allSchemas,
}: AntibodyConfigurationProps): JSX.Element {
  const formats = useAntibodyFormatsQuery();
  const selectedFormat = useMemo(() => {
    if (
      antibodyInterfaceImplementation == null ||
      antibodyInterfaceImplementation.interfaceId === UNSET_ANTIBODY_INTERFACE_ID
    ) {
      return null;
    } else {
      return formats.find(({id}) => id === antibodyInterfaceImplementation?.formatId) ?? null;
    }
  }, [formats, antibodyInterfaceImplementation]);

  if (antibodyInterfaceImplementation != null && formats.length === 0) {
    return <CenteredHorizontalThrobber />;
  }
  const formatsProps: Array<AntibodyProps> = formats.map((format) => {
    const restProps = ANTIBODY_REST_PROPS_BY_ID[format.id];
    return {
      format,
      ...restProps,
    };
  });

  const onToggleConfiguration = (): void => {
    if (antibodyInterfaceImplementation == null) {
      onChange({interfaceId: UNSET_ANTIBODY_INTERFACE_ID});
    } else {
      onChange(null);
    }
  };

  const onChangeFormat = (selectedFormat: string | null): void => {
    if (selectedFormat == null) {
      onChange({
        interfaceId: UNSET_ANTIBODY_INTERFACE_ID,
      });
    } else {
      onChange({
        interfaceId: ANTIBODY_SCHEMA_INTERFACE_ID,
        formatId: selectedFormat,
      });
    }
  };

  return (
    <>
      <div>
        <AntibodyConfigurationCheckbox
          checked={antibodyInterfaceImplementation != null}
          disabled={disabled}
          onChange={onToggleConfiguration}
        />
        {antibodyInterfaceImplementation != null && (
          <div className={css(styles.formatPickerWrapper)}>
            <Header3 className={css(styles.formatPickerHeader)}>Format</Header3>
            <FieldLabel label="Select format" />
            <AntibodyFormatPicker
              antibodyProps={formatsProps}
              disabled={disabled}
              selectedFormat={selectedFormat?.id ?? null}
              setSelectedFormat={onChangeFormat}
            />
            {selectedFormat != null && (
              <AntibodyConfigurationTable format={selectedFormat} allSchemas={allSchemas} />
            )}
          </div>
        )}
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  formatPickerWrapper: {
    borderRadius: '8px',
    backgroundColor: Color.GRAY_50,
    padding: '16px',
    marginBottom: '16px',
  },
  formatPickerHeader: {
    marginBottom: '16px',
  },
});
